import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { consumePremiumCheckoutIntent } from '../utils/postAuth';
import { useCurrency } from '../context/CurrencyContext';
import ListingCard from '../components/ListingCard';
import { MembershipBanner, MembershipActions, MemberChip } from '../components/MembershipStatus';
import UnreadBadge from '../components/UnreadBadge';
import PremiumBadge from '../components/PremiumBadge';
import ShareProfile from '../components/ShareProfile';
import { useMessages } from '../context/MessagesContext';
import sumInCurrency from '../utils/sumInCurrency';
import { formatMoney } from '../utils/fx';
import { Icon, T } from '../tokens';
import './Profile.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Profile() {
  const {
    user, updateMe, requestSellerRole, createPremiumCheckout,
    listCollections, createCollection, updateCollection, deleteCollection,
    setPublicationCollections,
  } = useAuth();
  const [sellerReqBusy, setSellerReqBusy] = useState(false);
  const [sellerReqMsg, setSellerReqMsg] = useState('');
  // Llega desde Upload cuando poner una pieza en venta disparó la solicitud
  // automática de vendedor.
  const location = useLocation();
  const autoSellerRequest = !!location.state?.sellerRequested;
  // Seguidores del vendedor (solo aplica a role 'seller'). null = aún sin cargar.
  const [followersCount, setFollowersCount] = useState(null);

  const handleSellerRequest = async () => {
    setSellerReqBusy(true); setSellerReqMsg('');
    try {
      await requestSellerRole();
      setSellerReqMsg('Solicitud enviada. Un admin la revisará pronto.');
    } catch (e) {
      setSellerReqMsg(e.response?.data?.message || 'Error enviando solicitud');
    } finally { setSellerReqBusy(false); }
  };
  const { t, i18n } = useTranslation();
  const { displayCurrency, rates } = useCurrency();
  const { unreadCount } = useMessages();
  const navigate = useNavigate();
  const SCALE_ALL = t('profile.scale_all');
  const SCALE_FILTERS = [SCALE_ALL, '1/18', '1/24', '1/43', '1/64', '1/87'];

  const [publications, setPublications] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [scale, setScale] = useState(SCALE_ALL);
  const [activeColl, setActiveColl] = useState(null); // null = all

  // Collection modals: collModal === null | 'new' | 'manage' | collectionObj
  const [collModal, setCollModal] = useState(null);
  const [collForm, setCollForm] = useState({ name: '', description: '', isPublic: true });
  const [collErr, setCollErr] = useState('');
  const [collBusy, setCollBusy] = useState(false);

  // Per-publication folder picker
  const [pubFoldersFor, setPubFoldersFor] = useState(null); // publication id
  const [pubFoldersSelected, setPubFoldersSelected] = useState(new Set());
  const [pubFoldersBusy, setPubFoldersBusy] = useState(false);

  useEffect(() => { setScale(SCALE_ALL); }, [SCALE_ALL]);

  // Journey Premium sin sesión: si el usuario llegó aquí tras registrarse con
  // la intención pendiente (botón "Comenzar ahora" en /precios), se lanza el
  // Stripe Checkout de inmediato. Si el checkout falla, el perfil queda
  // visible y puede suscribirse desde las acciones de membresía.
  const premiumIntentFired = useRef(false);
  const [premiumRedirecting, setPremiumRedirecting] = useState(false);
  useEffect(() => {
    if (!user || premiumIntentFired.current) return;
    if (!consumePremiumCheckoutIntent()) return;
    premiumIntentFired.current = true;
    if (user.role === 'user_premium') return; // ya es premium: no hay nada que cobrar
    setPremiumRedirecting(true);
    createPremiumCheckout()
      .then(({ url }) => window.location.assign(url))
      .catch(() => setPremiumRedirecting(false));
  }, [user, createPremiumCheckout]);

  const refreshPublications = useCallback(() => {
    setLoadingList(true);
    return axios.get(`${API}/api/publications/mine`)
      .then(({ data }) => setPublications(data.publications || []))
      .catch(() => setPublications([]))
      .finally(() => setLoadingList(false));
  }, []);

  const refreshCollections = useCallback(() => {
    return listCollections().then(setCollections).catch(() => setCollections([]));
  }, [listCollections]);

  useEffect(() => {
    if (!user) return;
    refreshPublications();
    refreshCollections();
  }, [user, refreshPublications, refreshCollections]);

  // Carga el número de seguidores para mostrarlo en el header (sellers y premium).
  useEffect(() => {
    if (!user || !['seller', 'user_premium'].includes(user.role)) { setFollowersCount(null); return; }
    let cancelled = false;
    axios.get(`${API}/api/follows/${user._id}/status`)
      .then(({ data }) => { if (!cancelled) setFollowersCount(data.followersCount ?? 0); })
      .catch(() => { if (!cancelled) setFollowersCount(0); });
    return () => { cancelled = true; };
  }, [user]);

  // Filtros combinados (carpeta + escala).
  const visible = publications.filter((p) => {
    if (activeColl && !(p.collections || []).map(String).includes(String(activeColl))) return false;
    if (scale !== SCALE_ALL && p.scale !== scale) return false;
    return true;
  });

  // ── Collection modals ─────────────────────────────────────
  const openNewColl = () => {
    setCollForm({ name: '', description: '', isPublic: true });
    setCollErr('');
    setCollModal('new');
  };
  const openEditColl = (c) => {
    setCollForm({ name: c.name, description: c.description || '', isPublic: c.isPublic !== false });
    setCollErr('');
    setCollModal(c);
  };
  const closeCollModal = () => { setCollModal(null); setCollErr(''); };

  const saveColl = async () => {
    setCollBusy(true); setCollErr('');
    try {
      if (collModal === 'new') {
        await createCollection(collForm);
      } else {
        await updateCollection(collModal._id, collForm);
      }
      await refreshCollections();
      closeCollModal();
    } catch (err) {
      setCollErr(err.response?.data?.message || 'Error');
    } finally { setCollBusy(false); }
  };

  const removeColl = async (c) => {
    if (!window.confirm(t('profile.collection_modal.delete_confirm'))) return;
    setCollBusy(true);
    try {
      await deleteCollection(c._id);
      if (activeColl === c._id) setActiveColl(null);
      await Promise.all([refreshCollections(), refreshPublications()]);
      closeCollModal();
    } catch (err) {
      setCollErr(err.response?.data?.message || 'Error');
    } finally { setCollBusy(false); }
  };

  // ── Per-publication folder picker ─────────────────────────
  const openPubFolders = (pub) => {
    setPubFoldersFor(pub._id);
    setPubFoldersSelected(new Set((pub.collections || []).map(String)));
  };
  const togglePubFolder = (collId) => {
    setPubFoldersSelected((prev) => {
      const next = new Set(prev);
      const k = String(collId);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };
  const savePubFolders = async () => {
    setPubFoldersBusy(true);
    try {
      const updated = await setPublicationCollections(pubFoldersFor, [...pubFoldersSelected]);
      setPublications((prev) => prev.map((p) => p._id === updated._id ? { ...p, collections: updated.collections } : p));
      await refreshCollections(); // refresh counts
      setPubFoldersFor(null);
    } catch {
      // mantener modal abierto si falla
    } finally { setPubFoldersBusy(false); }
  };

  const displayUser = user || {};
  const showValue = displayUser.showTotalValue !== false;
  // Suma todas las publicaciones convirtiendo cada una a la divisa preferida
  // del propio usuario antes de sumarlas. Si las tasas aún no cargan, sumInCurrency
  // se queda con los amounts en su divisa original.
  const totalValue = sumInCurrency(publications, displayCurrency, rates);

  const toggleShowValue = async () => {
    try { await updateMe({ showTotalValue: !showValue }); }
    catch { /* swallow — UI just won't toggle */ }
  };

  const stats = [
    {
      label: t('profile.total_value'),
      value: showValue ? formatMoney(totalValue, displayCurrency, i18n.language) : '••••',
      extra: <span className="stat-sub">{showValue ? t('profile.usd_estimated') : t('profile.hidden_public')}</span>,
      hasEye: true,
    },
    { label: t('profile.pieces'), value: String(publications.length), extra: <span className="stat-sub">{t('profile.in_garage')}</span> },
    { label: t('profile.for_sale'), value: String(publications.filter((p) => p.action === 'Venta').length), extra: <span className="stat-sub">{t('profile.active')}</span> },
    { label: t('profile.trade'), value: String(publications.filter((p) => p.action === 'Cambio').length), extra: <span className="stat-sub">{t('profile.available')}</span> },
  ];

  // Pantalla mínima mientras se redirige al checkout de Stripe (evita el
  // parpadeo del perfil completo justo antes de salir hacia la pasarela).
  if (premiumRedirecting) {
    return (
      <div className="profile">
        <div className="profile-body container" style={{ display: 'grid', placeItems: 'center', minHeight: '50vh' }}>
          <p style={{ color: 'var(--muted)', fontSize: 14 }}>{t('pricing.cta_loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile">
      <div className="profile-body container">
        {/* ── Estado de membresía / trial (Journey A) ── */}
        <MembershipBanner />

        {/* ── Header ── */}
        <header className="profile-header fade-up">
          <div className="profile-av-wrap">
            <img
              src={displayUser.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayUser.name || 'U')}&background=d4a58a&color=fff&size=128`}
              alt={displayUser.name} className="profile-av"
            />
            <span className="profile-online" />
          </div>
          <h1 className="profile-username">
            {displayUser.username || displayUser.name}
            {displayUser.role === 'user_premium' && (
              <span title="Usuario Premium Validado" style={{ display: 'inline-flex', marginLeft: 8 }}>
                <PremiumBadge size={28} />
              </span>
            )}
            {displayUser.role === 'seller' && (
              <span style={{
                marginLeft: 10, padding: '3px 10px', borderRadius: 20,
                background: 'rgba(20,184,166,.12)', color: 'var(--teal)',
                fontSize: 11, fontWeight: 700, letterSpacing: '.08em', verticalAlign: 'middle',
              }}>{t('profile.seller_badge')}</span>
            )}
          </h1>
          {displayUser.username && displayUser.name && displayUser.username !== displayUser.name && (
            <p className="profile-realname">{displayUser.name}</p>
          )}
          {displayUser.location && <p className="profile-loc">📍 {displayUser.location}</p>}
          {['seller', 'user_premium'].includes(displayUser.role) && followersCount !== null && (
            <p className="profile-followers">
              {Icon.userPlus(12)} {t('follow.followers_count', { count: followersCount })}
            </p>
          )}
          {displayUser.bio && <p className="profile-bio">{displayUser.bio}</p>}
          {displayUser.website && (
            <a
              className="profile-website"
              href={/^https?:\/\//i.test(displayUser.website) ? displayUser.website : `https://${displayUser.website}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {Icon.globe(12)} {displayUser.website.replace(/^https?:\/\//i, '')}
            </a>
          )}

          <MemberChip />

          <div className="profile-actions">
            <MembershipActions />
            <ShareProfile profileId={displayUser._id} displayName={displayUser.username || displayUser.name} />
            {user?.role === 'seller' || user?.sellerStatus === 'approved' ? (
              <Link to="/seller" className="btn-ghost" style={{ textDecoration: 'none' }}>
                {t('profile.seller_panel')}
              </Link>
            ) : user?.role === 'user_premium' ? (
              // Solicitar ser vendedor es exclusivo del plan Premium; el plan
              // gratuito (role 'user') no ve este botón.
              user?.sellerStatus === 'pending' ? (
                <span className="btn-ghost" style={{ cursor: 'default', opacity: .8 }}>
                  {t('profile.seller_request_pending')}
                </span>
              ) : (
                <button
                  className="btn-ghost"
                  onClick={handleSellerRequest}
                  disabled={sellerReqBusy}
                  title={user?.sellerStatus === 'rejected' ? t('profile.seller_request_rejected_title') : ''}
                >
                  {sellerReqBusy ? t('profile.seller_request_sending') : user?.sellerStatus === 'rejected' ? t('profile.seller_request_again') : t('profile.seller_request')}
                </button>
              )
            ) : null}
            <button
              className="pnav-btn"
              onClick={() => navigate('/mensajes')}
              title={t('messages.title')}
              aria-label={t('messages.title')}
              style={{ position: 'relative', width: 40, height: 40, border: `1px solid ${T.line}`, borderRadius: 10, background: T.surface }}
            >
              {Icon.message(16)}
              <UnreadBadge count={unreadCount} />
            </button>
          </div>
          {autoSellerRequest && user?.sellerStatus === 'pending' && (
            <div className="admin-flash admin-flash--ok" role="status" style={{ marginTop: 12, maxWidth: 560, textAlign: 'left' }}>
              {t('profile.seller_request_auto')}
            </div>
          )}
          {sellerReqMsg && (
            <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>{sellerReqMsg}</p>
          )}
        </header>

        {/* ── Stats ── */}
        <div className="stats-grid fade-up" style={{ animationDelay: '.08s' }}>
          {stats.map((s) => (
            <div key={s.label} className="stat-card">
              <div className="stat-card__top">
                <span className="stat-card__label">{s.label}</span>
                {s.hasEye && (
                  <button
                    type="button"
                    className="stat-eye stat-eye--btn"
                    onClick={toggleShowValue}
                    title={showValue ? t('profile.hide_public') : t('profile.show_public')}
                  >
                    {showValue ? Icon.eye(13) : Icon.eyeOff(13)}
                  </button>
                )}
              </div>
              <div className="stat-card__val">{s.value}</div>
              {s.extra}
            </div>
          ))}
        </div>

        {/* ── Collection chips ── */}
        <section className="profile-coll fade-up" style={{ animationDelay: '.12s' }}>
          <div className="coll-folders">
            <span className="coll-folders__label">{t('profile.collections_label')}</span>
            <button
              className={`chip${activeColl === null ? ' chip--active' : ''}`}
              onClick={() => setActiveColl(null)}
            >
              {t('profile.collection_all')}
            </button>
            {collections.map((c) => (
              <button
                key={c._id}
                className={`chip${activeColl === c._id ? ' chip--active' : ''}`}
                onClick={() => setActiveColl(c._id)}
                title={c.description || c.name}
              >
                {Icon.folder(12)} {c.name}
                {c.count > 0 && <span className="chip-count">{c.count}</span>}
              </button>
            ))}
            <button className="chip chip--ghost" onClick={openNewColl}>
              {t('profile.collection_new')}
            </button>
            {collections.length > 0 && (
              <button className="chip chip--ghost" onClick={() => setCollModal('manage')}>
                {t('profile.collection_manage')}
              </button>
            )}
          </div>
        </section>

        {/* ── Collection (publications grid) ── */}
        <section className="profile-coll fade-up" style={{ animationDelay: '.14s' }}>
          <div className="coll-header">
            <h2 className="coll-title">{t('profile.my_collection')}</h2>
            <div className="coll-filters">
              {SCALE_FILTERS.map((s) => (
                <button key={s} className={`chip${scale === s ? ' chip--active' : ''}`} onClick={() => setScale(s)}>{s}</button>
              ))}
            </div>
          </div>

          <div className="coll-grid">
            {loadingList && <p className="coll-empty">{t('profile.loading_collection')}</p>}
            {!loadingList && visible.length === 0 && (
              <p className="coll-empty">
                {publications.length === 0
                  ? t('profile.no_pieces')
                  : activeColl
                    ? t('profile.collection_empty_filter')
                    : t('profile.no_in_scale')}
                {publications.length === 0 && (
                  <Link to="/upload" style={{ color: 'var(--teal)' }}>{t('profile.upload_first')}</Link>
                )}
              </p>
            )}
            {!loadingList && visible.map((p) => (
              <div key={p._id} className="pub-cell">
                <ListingCard listing={p} />
                <button
                  type="button"
                  className="pub-folders-btn pub-edit-btn"
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigate(`/upload/${p._id}`); }}
                  title={t('profile.pub_edit_btn', { defaultValue: 'Editar pieza' })}
                >
                  ✎
                </button>
                <button
                  type="button"
                  className="pub-folders-btn"
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); openPubFolders(p); }}
                  title={t('profile.pub_folders_btn')}
                >
                  {Icon.folder(12)} {(p.collections || []).length || ''}
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* ── Collection create/edit modal ── */}
      {(collModal === 'new' || (collModal && typeof collModal === 'object')) && (
        <div className="modal-overlay" onClick={closeCollModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>{collModal === 'new' ? t('profile.collection_modal.new_title') : t('profile.collection_modal.edit_title')}</h3>
            {collErr && <div className="error-banner" style={{ marginTop: 10 }}>{collErr}</div>}

            <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginTop: 14, marginBottom: 6 }}>
              {t('profile.collection_modal.name_label')}
            </label>
            <input
              className="light-input"
              value={collForm.name}
              onChange={(e) => setCollForm((f) => ({ ...f, name: e.target.value }))}
              placeholder={t('profile.collection_modal.name_placeholder')}
              maxLength={60}
              autoFocus
            />

            <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginTop: 12, marginBottom: 6 }}>
              {t('profile.collection_modal.desc_label')}
            </label>
            <textarea
              className="light-input"
              style={{ minHeight: 60 }}
              value={collForm.description}
              onChange={(e) => setCollForm((f) => ({ ...f, description: e.target.value }))}
              maxLength={200}
            />

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={collForm.isPublic}
                onChange={(e) => setCollForm((f) => ({ ...f, isPublic: e.target.checked }))}
              />
              {t('profile.collection_modal.public_label')}
            </label>

            <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'space-between', alignItems: 'center' }}>
              {collModal !== 'new' ? (
                <button className="btn-ghost" style={{ color: '#ef4444' }} onClick={() => removeColl(collModal)} disabled={collBusy}>
                  {t('profile.collection_modal.delete')}
                </button>
              ) : <span />}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost" onClick={closeCollModal} disabled={collBusy}>{t('profile.collection_modal.cancel')}</button>
                <button className="btn-teal" onClick={saveColl} disabled={collBusy || !collForm.name.trim()}>
                  {collBusy ? t('profile.collection_modal.saving') : t('profile.collection_modal.save')}
                </button>
              </div>
            </div>
            <button className="modal-close" onClick={closeCollModal}>✕</button>
          </div>
        </div>
      )}

      {/* ── Manage collections list modal ── */}
      {collModal === 'manage' && (
        <div className="modal-overlay" onClick={closeCollModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>{t('profile.collection_manage')}</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: '14px 0 0', display: 'grid', gap: 8 }}>
              {collections.map((c) => (
                <li
                  key={c._id}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 10, background: 'var(--bg)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {Icon.folder(12)}
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {c.count || 0} · {c.isPublic ? '🌐' : '🔒'}
                      </div>
                    </div>
                  </div>
                  <button className="btn-ghost btn-sm" onClick={() => openEditColl(c)}>
                    ✎
                  </button>
                </li>
              ))}
            </ul>
            <button className="modal-close" onClick={closeCollModal}>✕</button>
          </div>
        </div>
      )}

      {/* ── Pub folders picker modal ── */}
      {pubFoldersFor && (
        <div className="modal-overlay" onClick={() => setPubFoldersFor(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>{t('profile.pub_folders_modal.title')}</h3>
            {collections.length === 0 ? (
              <p style={{ marginTop: 14, color: 'var(--muted)', fontSize: 13 }}>{t('profile.pub_folders_modal.empty')}</p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: '14px 0 0', display: 'grid', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
                {collections.map((c) => (
                  <li key={c._id}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 10, cursor: 'pointer', background: pubFoldersSelected.has(String(c._id)) ? 'rgba(20,184,166,.08)' : 'var(--bg)' }}>
                      <input
                        type="checkbox"
                        checked={pubFoldersSelected.has(String(c._id))}
                        onChange={() => togglePubFolder(c._id)}
                      />
                      {Icon.folder(12)}
                      <span style={{ fontSize: 13 }}>{c.name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
              <button className="btn-ghost" onClick={() => setPubFoldersFor(null)} disabled={pubFoldersBusy}>{t('profile.collection_modal.cancel')}</button>
              <button className="btn-teal" onClick={savePubFolders} disabled={pubFoldersBusy}>
                {pubFoldersBusy ? t('profile.pub_folders_modal.saving') : t('profile.pub_folders_modal.save')}
              </button>
            </div>
            <button className="modal-close" onClick={() => setPubFoldersFor(null)}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
}
